import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useApi } from '@/hooks/useApi';
import { useToast } from '@/hooks/use-toast';
import { Route, RouteDriver } from '@/types/route.types';
import { EditRouteModal, RouteInfoCard, RouteDriversList, AssignDriverModal } from '@/components/features/routes';
import { 
  ArrowRight, 
  MoreVertical, 
  RefreshCw, 
  Edit, 
  Copy, 
  Check,
  Route as RouteIcon,
  Clock,
  Users,
  MapPin,
  UserPlus
} from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

export default function RouteDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { get, loading } = useApi();
  const { tp } = useI18n();

  const [route, setRoute] = useState<Route | null>(null);
  const [drivers, setDrivers] = useState<RouteDriver[]>([]);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchRouteDetails = useCallback(async () => {
    if (!id) return;
    try {
      const response: any = await get(`/api/v1/settings/routes/${id}`);
      const data = response?.data || response;
      setRoute(data);
    } catch (error) {
      console.error('Error fetching route details:', error);
    } finally {
      setHasFetched(true);
    }
  }, [id]);

  const fetchRouteDrivers = useCallback(async () => {
    if (!id) return;
    try {
      const response: any = await get(`/api/v1/settings/routes/${id}/drivers`);
      const data = Array.isArray(response) ? response : response?.data || [];
      setDrivers(data);
    } catch (error) {
      console.error('Error fetching route drivers:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchRouteDetails();
    fetchRouteDrivers();
  }, [fetchRouteDetails, fetchRouteDrivers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchRouteDetails(), fetchRouteDrivers()]);
    setRefreshing(false);
    toast({ title: tp.routes.dataRefreshed });
  };

  const handleCopyId = () => {
    if (route?.id) {
      navigator.clipboard.writeText(route.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: tp.routes.copyIdSuccess });
    }
  };

  if (loading && !hasFetched) {
    return (
      <PageContainer>
        <LoadingSkeleton type="details" />
      </PageContainer>
    );
  }

  if (!route && hasFetched) {
    return (
      <PageContainer>
        <EmptyState
          icon={<RouteIcon className="h-16 w-16" />}
          title={tp.routes.notFound}
          description={tp.routes.notFoundDesc}
          action={{
            label: tp.routes.backToRoutes,
            onClick: () => navigate('/dashboard/settings/routes'),
          }}
        />
      </PageContainer>
    );
  }

  if (!route) return null;

  const activeDriversCount = drivers.filter(d => d.is_active).length;

  return (
    <PageContainer>
      <div className="space-y-6" dir="rtl">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard/settings/routes')}
              className="gap-1 p-0 hover:bg-transparent"
            >
              <ArrowRight className="h-4 w-4" />
              <span>{tp.routes.detailsBack}</span>
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <RouteIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold">{route.name}</h1>
                  <StatusBadge status={route.is_active ? tp.routes.active : tp.routes.inactive} />
                </div>
                <p className="text-muted-foreground font-mono">{route.code}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyId}
                title={tp.routes.copyId}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowEditModal(true)}>
                    <Edit className="ml-2 h-4 w-4" />
                    {tp.routes.editRoute}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <QuickStatCard
            icon={<MapPin className="h-5 w-5" />}
            value={route.estimated_distance_km ? `${route.estimated_distance_km} ${tp.routes.km}` : '-'}
            label={tp.routes.estimatedDistance}
          />
          <QuickStatCard
            icon={<Clock className="h-5 w-5" />}
            value={route.estimated_time_minutes ? `${route.estimated_time_minutes} ${tp.routes.minutes}` : '-'}
            label={tp.routes.estimatedTime}
          />
          <QuickStatCard
            icon={<Users className="h-5 w-5" />}
            value={activeDriversCount.toString()}
            label={tp.routes.assignedDrivers}
          />
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Route Info */}
          <div className="lg:col-span-1">
            <RouteInfoCard route={route} />
          </div>

          {/* Assigned Drivers */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {tp.routes.assignedDrivers}
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => setShowAssignModal(true)}
                  className="gap-1"
                >
                  <UserPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">{tp.routes.assignDriver}</span>
                </Button>
              </CardHeader>
              <CardContent>
                <RouteDriversList 
                  routeId={id!}
                  drivers={drivers} 
                  loading={loading && !hasFetched}
                  onDriverRemoved={fetchRouteDrivers}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <EditRouteModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={() => {
          fetchRouteDetails();
          setShowEditModal(false);
        }}
        route={route}
      />

      {/* Assign Driver Modal */}
      <AssignDriverModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onSuccess={() => {
          fetchRouteDrivers();
          setShowAssignModal(false);
        }}
        routeId={route.id}
        routeName={route.name}
        assignedDriverIds={drivers.map((d) => d.driver_profile?.id).filter(Boolean) as string[]}
      />
    </PageContainer>
  );
}

interface QuickStatCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
}

function QuickStatCard({ icon, value, label }: QuickStatCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
            {icon}
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-muted-foreground">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
